// Recurrence: parse/format a small RRULE subset and expand a series into
// concrete occurrences. Pure and dependency-free (native `Intl` via ./tz), so it
// runs identically on server and client.
//
// The one rule that makes this timezone-correct: a TIMED series is anchored to a
// WALL-CLOCK time in its ORIGIN zone (e.g. "09:00 Europe/Paris"). Each occurrence
// re-resolves that wall-clock to a UTC instant using the zone's DST rules for the
// occurrence's OWN date — so 09:00 stays 09:00 across a DST transition, and its
// UTC offset shifts. ALL-DAY series are zone-less: they step civil dates verbatim.
// See docs/timezones.md.

import { utcToZonedParts, zonedWallClockToUtc } from "@/platform/kit/tz.js";

export type Freq = "DAILY" | "WEEKLY" | "MONTHLY" | "YEARLY";
export type Weekday = "MO" | "TU" | "WE" | "TH" | "FR" | "SA" | "SU";

export interface RecurrenceRule {
  freq: Freq;
  interval: number; // >= 1 ("every N")
  byDay?: Weekday[]; // WEEKLY only — the weekdays to repeat on
  count?: number; // end after N occurrences (counted from the series start)
  until?: string; // end on/before this civil date (YYYY-MM-DD), inclusive
}

export interface RecurrenceBase {
  start: string; // ISO UTC of the master (first) occurrence
  end: string | null; // ISO UTC of the master end — the duration/span source
  allDay: boolean;
  timezone: string | null; // origin zone for a timed series; null → use fallback
  rule: string; // raw RRULE string
}

interface Occurrence {
  start: string; // ISO UTC
  end: string | null; // ISO UTC
}

const FREQS: Freq[] = ["DAILY", "WEEKLY", "MONTHLY", "YEARLY"];
// Monday-first indexing (WKST defaults to MO), matching the rest of the app.
const WEEKDAY_ORDER: Weekday[] = ["MO", "TU", "WE", "TH", "FR", "SA", "SU"];
const WEEKDAY_IDX: Record<Weekday, number> = { MO: 0, TU: 1, WE: 2, TH: 3, FR: 4, SA: 5, SU: 6 };

// Defensive ceiling on how many candidate dates the stepper will ever visit for
// one series+window, so a pathological rule (e.g. MONTHLY on the 31st) can't loop.
const MAX_STEPS = 100_000;

const pad2 = (n: number) => String(n).padStart(2, "0");

// --- parse / format -------------------------------------------------------

/** Parse an RRULE string into the supported subset, or null if unusable. */
export function parseRule(rrule: string): RecurrenceRule | null {
  const parts = new Map<string, string>();
  for (const seg of rrule
    .trim()
    .replace(/^RRULE:/i, "")
    .split(";")) {
    const eq = seg.indexOf("=");
    if (eq === -1) continue;
    parts.set(seg.slice(0, eq).trim().toUpperCase(), seg.slice(eq + 1).trim());
  }
  const freq = parts.get("FREQ")?.toUpperCase() as Freq | undefined;
  if (!freq || !FREQS.includes(freq)) return null;

  const rule: RecurrenceRule = { freq, interval: 1 };

  const interval = parts.get("INTERVAL");
  if (interval != null) {
    const n = parseInt(interval, 10);
    if (Number.isFinite(n) && n >= 1) rule.interval = n;
  }

  const byDay = parts.get("BYDAY");
  if (byDay) {
    const days = byDay
      .split(",")
      .map((d) => d.trim().toUpperCase())
      .filter((d): d is Weekday => (WEEKDAY_ORDER as string[]).includes(d));
    // BYDAY only affects WEEKLY here; ignore for other freqs (unsupported detail).
    if (freq === "WEEKLY" && days.length > 0) {
      rule.byDay = [...new Set(days)].sort((a, b) => WEEKDAY_IDX[a] - WEEKDAY_IDX[b]);
    }
  }

  const count = parts.get("COUNT");
  if (count != null) {
    const n = parseInt(count, 10);
    if (Number.isFinite(n) && n >= 1) rule.count = n;
  }

  const until = parts.get("UNTIL");
  if (until) {
    // RFC UNTIL is a DATE or DATE-TIME; we only need the civil date part.
    const m = until.match(/^(\d{4})-?(\d{2})-?(\d{2})/);
    if (m) rule.until = `${m[1]}-${m[2]}-${m[3]}`;
  }

  return rule;
}

/** Serialize a RecurrenceRule back to an RRULE string (COUNT wins over UNTIL). */
export function formatRule(rule: RecurrenceRule): string {
  const out = [`FREQ=${rule.freq}`];
  if (rule.interval > 1) out.push(`INTERVAL=${rule.interval}`);
  if (rule.freq === "WEEKLY" && rule.byDay?.length) out.push(`BYDAY=${rule.byDay.join(",")}`);
  if (rule.count != null) out.push(`COUNT=${rule.count}`);
  else if (rule.until) out.push(`UNTIL=${rule.until.replace(/-/g, "")}`);
  return out.join(";");
}

// --- civil-date stepping (calendar arithmetic on a UTC scratch date) ------

interface Civil {
  y: number;
  mo: number; // 1-12
  d: number;
}

const civilKey = (c: Civil) => c.y * 10000 + c.mo * 100 + c.d;

/** True when (y, mo, d) is a real calendar date (rejects e.g. Feb 30, Apr 31). */
function isRealDate(y: number, mo: number, d: number): boolean {
  const dt = new Date(Date.UTC(y, mo - 1, d));
  return dt.getUTCFullYear() === y && dt.getUTCMonth() === mo - 1 && dt.getUTCDate() === d;
}

function addDaysCivil(c: Civil, days: number): Civil {
  const dt = new Date(Date.UTC(c.y, c.mo - 1, c.d + days));
  return { y: dt.getUTCFullYear(), mo: dt.getUTCMonth() + 1, d: dt.getUTCDate() };
}

/** Monday-based weekday index (0 = Mon .. 6 = Sun) of a civil date. */
function weekdayIdx(c: Civil): number {
  return (new Date(Date.UTC(c.y, c.mo - 1, c.d)).getUTCDay() + 6) % 7;
}

/**
 * Ordered civil dates of a series, starting at `anchor` (the master occurrence's
 * civil date), applying FREQ/INTERVAL/BYDAY. `emit(date)` is called for each in
 * ascending order until it returns false (the caller stops on window/count/until).
 */
function stepCivilDates(anchor: Civil, rule: RecurrenceRule, emit: (c: Civil) => boolean): void {
  const { freq, interval } = rule;
  let steps = 0;

  if (freq === "WEEKLY" && rule.byDay?.length) {
    const anchorKey = civilKey(anchor);
    // Monday of the anchor's week; blocks advance interval weeks at a time.
    let monday = addDaysCivil(anchor, -weekdayIdx(anchor));
    while (steps++ < MAX_STEPS) {
      for (const wd of rule.byDay) {
        const date = addDaysCivil(monday, WEEKDAY_IDX[wd]);
        if (civilKey(date) < anchorKey) continue; // before the series start
        if (!emit(date)) return;
      }
      monday = addDaysCivil(monday, interval * 7);
    }
    return;
  }

  if (freq === "DAILY" || freq === "WEEKLY") {
    const stepDays = freq === "DAILY" ? interval : interval * 7;
    let date = anchor;
    while (steps++ < MAX_STEPS) {
      if (!emit(date)) return;
      date = addDaysCivil(date, stepDays);
    }
    return;
  }

  if (freq === "MONTHLY") {
    for (let k = 0; steps++ < MAX_STEPS; k++) {
      const total = anchor.mo - 1 + k * interval;
      const y = anchor.y + Math.floor(total / 12);
      const mo = (total % 12) + 1;
      if (isRealDate(y, mo, anchor.d)) {
        if (!emit({ y, mo, d: anchor.d })) return;
      }
      // months without the anchor day (e.g. the 31st) are simply skipped
    }
    return;
  }

  // YEARLY
  for (let k = 0; steps++ < MAX_STEPS; k++) {
    const y = anchor.y + k * interval;
    if (isRealDate(y, anchor.mo, anchor.d)) {
      if (!emit({ y, mo: anchor.mo, d: anchor.d })) return;
    }
    // Feb 29 in a non-leap year is skipped
  }
}

// --- expansion ------------------------------------------------------------

/**
 * Expand a recurring event into the occurrences overlapping [windowFrom, windowTo)
 * (both ISO UTC, `to` exclusive). Returns [] when the rule is unusable (caller
 * should then treat the base event as a single occurrence). COUNT/UNTIL are
 * evaluated from the series start, independent of the window. `fallbackZone` is
 * used for a timed series stored without a `timezone`.
 */
export function expandOccurrences(
  base: RecurrenceBase,
  windowFrom: string,
  windowTo: string,
  fallbackZone: string,
): Occurrence[] {
  const rule = parseRule(base.rule);
  if (!rule) return [];

  const fromMs = Date.parse(windowFrom);
  const toMs = Date.parse(windowTo);
  const startMs = Date.parse(base.start);
  if (!Number.isFinite(fromMs) || !Number.isFinite(toMs) || !Number.isFinite(startMs)) return [];

  const out: Occurrence[] = [];

  if (base.allDay) {
    // Zone-less: step civil dates, keep the master's day-span, emit verbatim.
    const anchor = civilFromIsoDate(base.start);
    const endMs = base.end ? Date.parse(base.end) : startMs;
    const spanDays = Math.max(0, Math.round((endMs - startMs) / 86_400_000));
    let seq = 0;
    stepCivilDates(anchor, rule, (c) => {
      if (rule.until && civilKey(c) > civilKey(civilFromIsoDate(`${rule.until}T00:00:00Z`)))
        return false;
      if (rule.count != null && seq >= rule.count) return false;
      seq++;
      const occStartMs = Date.UTC(c.y, c.mo - 1, c.d);
      if (occStartMs >= toMs) return false; // past the window — and every later one is too
      if (occStartMs >= fromMs || (spanDays > 0 && occStartMs + spanDays * 86_400_000 > fromMs)) {
        const end = addDaysCivil(c, spanDays);
        out.push({
          start: `${c.y}-${pad2(c.mo)}-${pad2(c.d)}T00:00:00.000Z`,
          end: base.end ? `${end.y}-${pad2(end.mo)}-${pad2(end.d)}T00:00:00.000Z` : null,
        });
      }
      return true;
    });
    return out;
  }

  // Timed: recover the origin-zone wall-clock, re-resolve it per occurrence date.
  const zone = base.timezone || fallbackZone;
  const p = utcToZonedParts(new Date(startMs), zone);
  const anchor: Civil = { y: p.year, mo: p.month, d: p.day };
  const durationMs = base.end ? Math.max(0, Date.parse(base.end) - startMs) : 0;
  const untilMs = rule.until
    ? zonedWallClockToUtc(
        ...civilTriple(civilFromIsoDate(`${rule.until}T00:00:00Z`)),
        23,
        59,
        59,
        zone,
      ).getTime()
    : null;

  let seq = 0;
  stepCivilDates(anchor, rule, (c) => {
    const occStart = zonedWallClockToUtc(
      c.y,
      c.mo,
      c.d,
      p.hour,
      p.minute,
      p.second,
      zone,
    ).getTime();
    if (untilMs != null && occStart > untilMs) return false;
    if (rule.count != null && seq >= rule.count) return false;
    seq++;
    if (occStart >= toMs) return false; // past the window — later ones are too
    if (occStart + durationMs > fromMs) {
      out.push({
        start: new Date(occStart).toISOString(),
        end: base.end ? new Date(occStart + durationMs).toISOString() : null,
      });
    }
    return true;
  });
  return out;
}

function civilFromIsoDate(iso: string): Civil {
  return { y: +iso.slice(0, 4), mo: +iso.slice(5, 7), d: +iso.slice(8, 10) };
}

function civilTriple(c: Civil): [number, number, number] {
  return [c.y, c.mo, c.d];
}
